import { KeyValuePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';

import { Store } from '@ngrx/store';

import { deleteAiDocument, removeUploadingDocument } from '@lib/medical-documents/core/store/medical-documents.actions';
import { selectAiDocumentsList, selectFilteredAiDocuments, selectIsAiSearchChecked, selectUploadingDocuments } from '@lib/medical-documents/core/store/medical-documents.selectors';
import { formatAiDocumentsByYear, sortYearsDescending } from '@lib/medical-documents/core/utils/document-formatters';

import { AiMedicalDocumentItem, DocumentOrigin, DocumentProcessingStatus, UploadingMedicalDocumentItem } from '../../../core/models/document.interface';
import { AiListItemComponent } from './ai-list-item/ai-list-item.component';

@Component({
  selector: 'md-ai-list',
  standalone: true,
  imports: [AiListItemComponent, KeyValuePipe],
  template: `
    <div class="flex flex-col gap-2">
      @if (uploadingDocuments().length > 0) {
        <div class="flex flex-col gap-2">
          @for (uploadingDocument of uploadingDocuments(); track uploadingDocument.id) {
            <md-ai-document-item
              [aiDocument]="uploadingDocumentToDocumentItem(uploadingDocument)"
              (documentDeleted)="removeUploadingDocument(uploadingDocument)"></md-ai-document-item>
          }
        </div>
      }
      @for (item of formattedDocuments() | keyvalue: reverseOrder; track item.key) {
        <div>
          <h3 class="text-neutral-700 mb-2 text-sm font-semibold">{{ item.key }}</h3>
          <div class="flex flex-col gap-2">
            @for (document of item.value; track document) {
              <md-ai-document-item [aiDocument]="document" (documentDeleted)="deleteMedicalDocument(document)"></md-ai-document-item>
            }
          </div>
        </div>
      }
    </div>
  `,
  styles: `
    :host {
      @apply h-full;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiDocumentsListComponent {
  private readonly store = inject(Store);

  protected readonly isAiSearchChecked = this.store.selectSignal<boolean>(selectIsAiSearchChecked);
  protected readonly allAiDocuments = this.store.selectSignal<AiMedicalDocumentItem[]>(selectAiDocumentsList);
  protected readonly filteredAiDocuments = this.store.selectSignal<AiMedicalDocumentItem[]>(selectFilteredAiDocuments);
  protected readonly uploadingDocuments = this.store.selectSignal<UploadingMedicalDocumentItem[]>(selectUploadingDocuments);

  protected readonly aiDocuments = computed(() => (this.isAiSearchChecked() ? this.allAiDocuments() : this.filteredAiDocuments()));
  protected readonly formattedDocuments = computed(() => formatAiDocumentsByYear(this.aiDocuments()));

  protected reverseOrder = sortYearsDescending;

  protected uploadingDocumentToDocumentItem(uploadingDocument: UploadingMedicalDocumentItem): AiMedicalDocumentItem {
    return {
      id: uploadingDocument.id || '',
      userId: uploadingDocument.userId || '',
      appointmentId: undefined,
      healthManagerTags: [],
      fileLink: uploadingDocument.fileLink || '',
      fileSize: uploadingDocument.file?.size || 0,
      fileName: uploadingDocument.file?.name || '',
      type: uploadingDocument.type,
      documentTitle: uploadingDocument.documentName || uploadingDocument.file.name,
      documentDate: uploadingDocument.createdAt,
      createdAt: uploadingDocument.createdAt || new Date().toISOString(),
      updatedAt: uploadingDocument.createdAt || new Date().toISOString(),
      processingStatus: (uploadingDocument.processingStatus as DocumentProcessingStatus) || DocumentProcessingStatus.InProgress,
      uploadedBy: DocumentOrigin.User,
      isEditable: false,
      isDraft: !uploadingDocument.uploadResponse,
      executedServices: [],
      plannedServices: [],
      plannedMedications: [],
      gatheredObservations: [],
      recommendations: [],
      notes: [],
      diagnoses: [],
      keywords: [],
      isMigration: false,
      fileHash: '',
    } as AiMedicalDocumentItem;
  }

  protected deleteMedicalDocument(item: AiMedicalDocumentItem) {
    this.store.dispatch(deleteAiDocument({ document: item }));
  }

  protected removeUploadingDocument(item: UploadingMedicalDocumentItem) {
    this.store.dispatch(removeUploadingDocument({ uploadingDocument: item }));
  }
}
